import { memo, useState, useEffect } from "react";
import styled from "styled-components";
import { Modal, Input, Button, DatePicker, Select } from "antd";
import { ClearOutlined } from "@ant-design/icons";
import { PiLightbulb } from "react-icons/pi";
import { useAtomValue, useSetAtom } from "jotai";
import { projectMembersWithRolesAtom, projectMembersWithRolesAsyncAtom } from "~/components/Map/Member/MemberStore";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

interface CollectItemFilterPopupProps {
  visible: boolean;
  onClose: () => void;
  onApplyFilter: (filters: FilterOptions) => void;
  formData?: any;
}

export interface FilterOptions {
  searchText?: string;
  dateRange?: [string, string] | null;
  syncDateRange?: [string, string] | null;
  creatorIds?: number[];
  selectedFields?: { [key: string]: string };
}

const CollectItemFilterPopup = ({
  visible,
  onClose,
  onApplyFilter,
  formData,
}: CollectItemFilterPopupProps) => {
  const [filters, setFilters] = useState<FilterOptions>({
    searchText: '',
    dateRange: null,
    syncDateRange: null,
    creatorIds: [],
    selectedFields: {},
  });

  // Get members from Jotai store and fetch function
  const memberWithRoles = useAtomValue(projectMembersWithRolesAtom);
  const fetchMembers = useSetAtom(projectMembersWithRolesAsyncAtom);

  // Auto-fetch members when popup opens and members list is empty
  useEffect(() => {
    if (visible && memberWithRoles.length === 0) {
      fetchMembers();
    }
  }, [visible, memberWithRoles.length, fetchMembers]);

  const handleApply = () => {
    onApplyFilter(filters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters: FilterOptions = {
      searchText: '',
      dateRange: null,
      syncDateRange: null,
      creatorIds: [],
      selectedFields: {},
    };
    setFilters(clearedFilters);
    onApplyFilter(clearedFilters);
  };

  const handleCancel = () => {
    onClose();
  };

  // Get dynamic fields from form schema
  const getDynamicFields = () => {
    if (!formData?.content) return [];

    let formContent = formData.content;
    if (typeof formContent === 'string') {
      try {
        formContent = JSON.parse(formContent);
      } catch (e) {
        console.error('Failed to parse form content:', e);
        return [];
      }
    }

    if (!Array.isArray(formContent)) return [];

    return formContent.filter((field: any) =>
      (field.type === 'text' ||
      field.type === 'textarea' ||
      field.type === 'select' ||
      !field.type) &&
      field.key !== 'name' && // Exclude 'name' field
      field.label !== 'Tên'   // Exclude fields with label 'Tên'
    ).slice(0, 3); // Limit to 3 most important fields
  };

  // Extract unique creators from memberWithRoles
  const getUniqueCreators = () => {
    return memberWithRoles
      .map((member: any) => ({
        id: member.user?.id,
        name: member.user?.fullname || member.user?.username || `User ${member.user?.id}`,
      }))
      .filter((creator) => creator.id)
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  };

  return (
    <Modal
      title="Lọc dữ liệu thu thập"
      open={visible}
      onCancel={handleCancel}
      width={500}
      footer={[
        <Button key="clear" icon={<ClearOutlined />} onClick={handleClear}>
          Xóa bộ lọc
        </Button>,
        <Button key="cancel" onClick={handleCancel}>
          Hủy
        </Button>,
        <Button key="apply" type="primary" onClick={handleApply}>
          Áp dụng
        </Button>,
      ]}
    >
      <UI_Content>
        {/* Text search */}
        <UI_FilterGroup>
          <UI_Label>Tìm trong tên:</UI_Label>
          <Input
            placeholder="Nhập từ khóa tìm kiếm..."
            value={filters.searchText}
            onChange={(e) => setFilters({ ...filters, searchText: e.target.value })}
            allowClear
          />
        </UI_FilterGroup>

        {/* Date range */}
        <UI_FilterGroup>
          <UI_Label>Khoảng thời gian tạo:</UI_Label>
          <RangePicker
            style={{ width: '100%' }}
            placeholder={['Từ ngày', 'Đến ngày']}
            value={
              filters.dateRange
                ? [dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])]
                : null
            }
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setFilters({
                  ...filters,
                  dateRange: [
                    dates[0].format('YYYY-MM-DD'),
                    dates[1].format('YYYY-MM-DD')
                  ]
                });
              } else {
                setFilters({ ...filters, dateRange: null });
              }
            }}
          />
        </UI_FilterGroup>

        {/* Sync date range */}
        <UI_FilterGroup>
          <UI_Label>Khoảng thời gian đồng bộ:</UI_Label>
          <RangePicker
            style={{ width: '100%' }}
            placeholder={['Từ ngày', 'Đến ngày']}
            value={
              filters.syncDateRange
                ? [dayjs(filters.syncDateRange[0]), dayjs(filters.syncDateRange[1])]
                : null
            }
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setFilters({
                  ...filters,
                  syncDateRange: [
                    dates[0].format('YYYY-MM-DD'),
                    dates[1].format('YYYY-MM-DD')
                  ]
                });
              } else {
                setFilters({ ...filters, syncDateRange: null });
              }
            }}
          />
        </UI_FilterGroup>

        {/* Creator MultiSelect */}
        <UI_FilterGroup>
          <UI_Label>Người tạo:</UI_Label>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Chọn người tạo dữ liệu..."
            value={filters.creatorIds}
            onChange={(value) => setFilters({ ...filters, creatorIds: value })}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={getUniqueCreators().map((creator: any) => ({
              label: creator.name,
              value: creator.id
            }))}
            maxTagCount="responsive"
          />
        </UI_FilterGroup>

        {/* Dynamic form fields */}
        {/* {getDynamicFields().map((field: any) => (
          <UI_FilterGroup key={field.key}>
            <UI_Label>{field.label || field.key}:</UI_Label>
            {field.type === 'select' && field.options ? (
              <Select
                style={{ width: '100%' }}
                placeholder={`Chọn ${field.label || field.key}...`}
                value={filters.selectedFields?.[field.key] || undefined}
                onChange={(value) => setFilters({
                  ...filters,
                  selectedFields: {
                    ...filters.selectedFields,
                    [field.key]: value
                  }
                })}
                allowClear
                options={field.options.map((option: any) => ({
                  label: option.label || option.value,
                  value: option.value
                }))}
              />
            ) : (
              <Input
                placeholder={`Nhập ${field.label || field.key}...`}
                value={filters.selectedFields?.[field.key] || ''}
                onChange={(e) => setFilters({
                  ...filters,
                  selectedFields: {
                    ...filters.selectedFields,
                    [field.key]: e.target.value
                  }
                })}
                allowClear
              />
            )}
          </UI_FilterGroup>
        ))} */}

        <UI_FilterInfo>
          <PiLightbulb style={{ verticalAlign: '-2px', marginRight: 4, fontSize: '14px', color: '#faad14' }} />
          Tip: Các bộ lọc sẽ được áp dụng đồng thời. Để tìm kiếm chính xác hơn,
          hãy sử dụng nhiều tiêu chí lọc cùng lúc.
        </UI_FilterInfo>
      </UI_Content>
    </Modal>
  );
};

export default memo(CollectItemFilterPopup);

const UI_Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
`;

const UI_FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UI_Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const UI_FilterInfo = styled.div`
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 12px;
  border-radius: 6px;
  line-height: 1.4;
  margin-top: 8px;
`;